import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { 
  Button, 
  InputField, 
  Help,
  CircularLoader,
  AlertBar,  
  SingleSelect, 
  SingleSelectOption,
} from '@dhis2/ui';
import { registerUser, verifyUsername } from '../../../utils/api';
import { useNavigate } from 'react-router-dom';

// Hardcoded gender options
const GENDER_OPTIONS = [
  { code: 'male', name: 'Male' },
  { code: 'female', name: 'Female' },
  { code: 'other', name: 'Other' },
  { code: 'prefer_not_to_say', name: 'Prefer not to say' }
];

const UserRegistration = ({ onSuccess = () => {}, initialData = {}, adminCredentials }) => {
  const auth = useAuth();
  const isAuthenticated = Boolean(auth?.me);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    gender: '',
    password: '',
    email: '',
    confirmPassword: '',
    phoneNumber: ''
  });

  const [errors, setErrors] = useState({});
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (value) {
      setErrors(prev => ({ ...prev, [field]: null }));
      if (field === 'username') {
        setNotification(null);
      }
    }
  };

  const validateUsername = useCallback(async (username) => {
    if (username.length >= 4) {
      try {
        const available = await verifyUsername(username, !isAuthenticated);
        setUsernameAvailable(available);
        
        if (!available) {
          setNotification({
            type: 'error',
            message: 'Username not available',
            details: 'The username is already taken. Please choose another one.'
          });
        } else {
          setNotification(null);
        }
      } catch (error) {
        console.error('Username check failed:', error);
        setNotification({
          type: 'error',
          message: isAuthenticated 
            ? 'Username check failed' 
            : 'Username verification unavailable',
          details: isAuthenticated
            ? 'Unable to verify username availability. Please try again.'
            : 'You may proceed, but username availability cannot be verified.'
        });
        setUsernameAvailable(true);
      }
    } else if (username.length > 0) {
      setNotification({
        type: 'error',
        message: 'Invalid username',
        details: 'Username must be at least 4 characters'
      });
      setUsernameAvailable(false);
    } else {
      setNotification(null);
      setUsernameAvailable(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
  if (formData.username) {
    const timer = setTimeout(() => {
      validateUsername(formData.username);
    }, 500);
    return () => clearTimeout(timer);
  } else {
    setNotification(null);
    setUsernameAvailable(true);
    setErrors((prev) => ({ ...prev, username: null }));  // Clear validation error for username when empty
  }
}, [formData.username, validateUsername]);
;

  const validateForm = () => {
    const errors = [];
    if (!formData.firstName.trim()) errors.push('First name is required');
    if (!formData.lastName.trim()) errors.push('Last name is required');
    if (!/^[a-zA-Z0-9_]{4,}$/.test(formData.username)) {
      errors.push('Username must be at least 4 characters (letters, numbers, underscores)');
    } else if (!usernameAvailable) {
      errors.push('Username already taken');
    }
    if (formData.password.length < 8) {
      errors.push('Password must be at least 8 characters');
    } else if (!/[A-Z]/.test(formData.password) || 
               !/[a-z]/.test(formData.password) || 
               !/[0-9]/.test(formData.password)) {
      errors.push('Password must contain uppercase, lowercase and numbers');
    }
    if (formData.password !== formData.confirmPassword) {
      errors.push('Passwords do not match');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotification(null);

    const errors = validateForm();
    if (errors.length > 0) {
      setNotification({
        type: 'error',
        message: 'Validation errors',
        details: errors.join('; ')
      });
      setLoading(false);
      return;
    }

    try {
        const registrationResponse = await registerUser({
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          password: formData.password,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          gender: formData.gender,
        });

        if (registrationResponse.token) {
          localStorage.setItem('authToken', registrationResponse.token);
        }

        setNotification({
          type: 'success',
          message: 'Registration successful!'
        });

        onSuccess({
          username: formData.username,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          ...registrationResponse
        });

        // FIX: Pass username and password as separate args, NOT an object
        const loginSuccess = await auth.login(formData.username, formData.password);

        if (loginSuccess) {
          // Navigate immediately after login success
          navigate('/organisation', { replace: true });
        } else {
          setNotification({
            type: 'error',
            message: 'Login after registration failed. Please try logging in manually.'
          });
        }

      } catch (error) {
        console.error('Registration error:', error);
        setNotification({
          type: 'error',
          message: 'Registration failed',
          details: error.message || 'Please try again'
        });
      } finally {
        setLoading(false);
      }

    };

  const renderFormField = (label, children, required = true) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontWeight: '500', marginBottom: '4px' }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </div>
      {children}
    </div>
  );

  const errorStyle = { color: 'red', fontSize: '0.8rem', marginTop: '-0.75rem', marginBottom: '0.75rem' };

  return (
    <div style={{ 
      padding: '24px',
      border: '1px solid #e0e0e0',
      borderRadius: '4px',
      backgroundColor: '#f8f8f8',
      boxShadow: '0 6px 10px rgba(0,0,0,0.1)'
    }}>
      <div className="registration-form">
        <h2 style={{ color: '#4CAF50' }}>Get Started</h2> 
        <h3>Create an Account to Get Start</h3>
        <Help>
          {adminCredentials 
            ? "Register an organization administrator account."
            : "Self-register for a new user account."}
        </Help>

        {notification && (
          <AlertBar {...notification.type === 'success' ? {success: true} : {critical: true}}>
            {notification.message}
            {notification.details && (
              <div style={{marginTop: '4px'}}>{notification.details}</div>
            )}
          </AlertBar>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {renderFormField('First Name', (
              <>
                <InputField
                  value={formData.firstName}
                  onChange={({ value }) => handleChange('firstName', value)}
                  required
                  disabled={loading}
                />
                {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
              </>
            ))}

            {renderFormField('Last Name', (
              <>
                <InputField
                  value={formData.lastName}
                  onChange={({ value }) => handleChange('lastName', value)}
                  required
                  disabled={loading}
                />
                {errors.lastName && <div style={errorStyle}>{errors.lastName}</div>}
              </>
            ))}

            {renderFormField('Username', (
              <>
                <InputField
                  value={formData.username}
                  onChange={({ value }) => handleChange('username', value)}
                  required
                  validationText={
                    formData.username.length > 0 && !usernameAvailable
                      ? 'Username not available'
                      : '4+ chars, letters, numbers, underscores'
                  }
                  error={!usernameAvailable}
                  disabled={loading}
                />
                {errors.username && <div style={errorStyle}>{errors.username}</div>}
              </>
            ))}

            {renderFormField('Email', (
              <>
                <InputField
                  type="email"
                  value={formData.email}
                  onChange={({ value }) => handleChange('email', value)}
                  required
                  disabled={loading}
                />
                {errors.email && <div style={errorStyle}>{errors.email}</div>}
              </>
            ))}

            {renderFormField('Phone Number', (
              <>
                <InputField
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={({ value }) => handleChange('phoneNumber', value)}
                  required
                  disabled={loading}
                />
                {errors.phoneNumber && <div style={errorStyle}>{errors.phoneNumber}</div>}
              </>
            ))}

            {renderFormField('Gender', (
              <>
                <SingleSelect
                  selected={formData.gender}
                  onChange={({ selected }) => handleChange('gender', selected)}
                >
                  <SingleSelectOption label="–Select Gender–" value="" />
                  {GENDER_OPTIONS.map(option => (
                    <SingleSelectOption
                      key={option.code}
                      value={option.code}
                      label={option.name}
                    />
                  ))}
                </SingleSelect>
                {errors.gender && <div style={errorStyle}>{errors.gender}</div>}
              </>
            ))}

            {renderFormField('Password', (
              <>
                <InputField
                  type="password"
                  value={formData.password}
                  onChange={({value}) => handleChange('password', value)}
                  required
                  validationText="8+ chars with uppercase, lowercase, numbers"
                  disabled={loading}
                />
                {errors.password && <div style={errorStyle}>{errors.password}</div>}
              </>
            ))}

            {renderFormField('Confirm Password', (
              <>
                <InputField
                  type="password"
                  value={formData.confirmPassword}
                  onChange={({value}) => handleChange('confirmPassword', value)}
                  required
                  disabled={loading}
                />
                {errors.confirmPassword && <div style={errorStyle}>{errors.confirmPassword}</div>}
              </>
            ))}
          </div>

          <Button
            type="submit"
            primary
            icon={loading ? <CircularLoader small /> : null}
            disabled={loading}
            className="submit-button"
          >
            {loading ? 'Processing...' : 'Register'}
          </Button>
        </form>
      <style>
        {`
          .registration-form {
            max-width: 800px;
            margin: 0 auto;
            padding: 24px;
          }
          .form-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
            margin-bottom: 16px;
          }
          .submit-button {
            margin-top: 24px;
          }
        `}
      </style>

      </div>
    </div>
  );
};

export default UserRegistration;